import type { TokenCredential } from "@azure/identity";
import { select } from "@inquirer/prompts";
import { authedJsonPagedValues } from "./http.js";
import { LocalCliError } from "./errors.js";

const ARM = "https://management.azure.com";
const ARM_SCOPE = "https://management.azure.com/.default";
const ACCOUNTS_API = "2024-10-01";
const PROJECTS_API = "2025-04-01-preview";

export interface FoundryProject {
  accountName: string;
  accountScope: string; // ARM resource id of the account (the RBAC grant scope)
  projectName: string;
  region: string;
  endpoint: string; // data-plane: https://<account>.services.ai.azure.com/api/projects/<project>
  projectPrincipalId: string | null; // the project MI (for the AcrPull check)
}

interface AccountEntry {
  name?: string;
  kind?: string;
  location?: string;
  id?: string;
}
interface ProjectEntry {
  name?: string;
  identity?: { principalId?: string };
  id?: string;
}

async function listProjectsForAccount(
  credential: TokenCredential,
  accountId: string,
): Promise<ProjectEntry[]> {
  const url = `${ARM}${accountId}/projects?api-version=${PROJECTS_API}`;
  return await authedJsonPagedValues<ProjectEntry>({ credential, scope: ARM_SCOPE, url });
}

/** Format a project candidate for display — includes account so identically-named projects are distinguishable. */
export function formatProjectCandidate(c: FoundryProject): string {
  return `${c.projectName} (account=${c.accountName}, ${c.region})`;
}

/**
 * Discover the target Foundry project by enumerating AIServices accounts and
 * their projects in the subscription: single → use it; multiple → interactive
 * select or a FOUNDRY_PROJECT_MULTIPLE error. When `endpoint` is given it is
 * matched against the discovered candidates (it does NOT skip enumeration —
 * an earlier version of this comment claimed it did, which made the
 * FOUNDRY_PROJECT_ENDPOINT_NOT_FOUND path very confusing to debug).
 *
 * The enumeration MUST be paginated. ARM splits list results with `nextLink`
 * and may return EMPTY early pages that carry only a continuation token; a
 * single-page read then finds zero accounts and every endpoint is rejected —
 * including a project created seconds earlier. See authedJsonPagedValues.
 */
export async function resolveFoundryProject(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  interactive: boolean;
  endpoint?: string;
  agentEndpoint?: string; // when supplied, auto-picks the matching candidate — skips interactive prompt
}): Promise<FoundryProject> {
  const candidates: FoundryProject[] = [];

  const accounts = await authedJsonPagedValues<AccountEntry>({
    credential: opts.credential,
    scope: ARM_SCOPE,
    url: `${ARM}/subscriptions/${opts.subscriptionId}/providers/Microsoft.CognitiveServices/accounts?api-version=${ACCOUNTS_API}`,
  });

  for (const acc of accounts) {
    if (acc.kind !== "AIServices" || !acc.name || !acc.id) continue;
    const projects = await listProjectsForAccount(opts.credential, acc.id);
    for (const proj of projects) {
      if (!proj.name) continue;
      // ARM returns the project's qualified name "<account>/<project>"; the
      // data-plane endpoint wants only the bare project segment.
      const projectName = proj.name.includes("/")
        ? proj.name.slice(proj.name.lastIndexOf("/") + 1)
        : proj.name;
      candidates.push({
        accountName: acc.name,
        accountScope: acc.id,
        projectName,
        region: acc.location ?? "",
        endpoint: `https://${acc.name}.services.ai.azure.com/api/projects/${projectName}`,
        projectPrincipalId: proj.identity?.principalId ?? null,
      });
    }
  }

  // Explicit --endpoint: match a discovered candidate, else fail clearly.
  if (opts.endpoint) {
    const match = candidates.find((c) => c.endpoint === opts.endpoint);
    if (match) return match;
    throw new LocalCliError({
      code: "FOUNDRY_PROJECT_ENDPOINT_NOT_FOUND",
      message: `--endpoint '${opts.endpoint}' did not match any discovered Foundry project in subscription ${opts.subscriptionId}.`,
      hint: `Discovered: ${candidates.map((c) => c.endpoint).join(", ") || "(none)"}.`,
    });
  }

  // When the caller supplies the agent's projectEndpoint (read from
  // per-agent yaml), auto-pick the candidate that matches. Skips the
  // interactive prompt + the FOUNDRY_PROJECT_MULTIPLE error for operators
  // with > 1 Foundry project in their subscription.
  //
  // Precedence ladder:
  //   1. opts.endpoint (explicit --endpoint flag) — handled above, always wins.
  //   2. opts.agentEndpoint (from yaml) AND ∈ candidates → use it.
  //   3. 0 candidates → error.
  //   4. 1 candidate → use it.
  //   5. > 1 candidates → interactive prompt (TTY) or FOUNDRY_PROJECT_MULTIPLE error.
  //
  // When agentEndpoint is set but doesn't match any candidate, fall through
  // to step 3/4/5 — the yaml points at a project that no longer exists in
  // this subscription; the operator gets a clean error or interactive pick.
  if (opts.agentEndpoint) {
    const match = candidates.find((c) => c.endpoint === opts.agentEndpoint);
    if (match) return match;
  }

  if (candidates.length === 0) {
    throw new LocalCliError({
      code: "FOUNDRY_PROJECT_ZERO",
      message: `No AIServices Foundry project found in subscription ${opts.subscriptionId}.`,
      hint: "Create one with 'm8t foundry create --resource-group <rg> --location <region>', switch subscription with 'az account set --subscription <id>', or pass --endpoint.",
    });
  }
  if (candidates.length === 1) return candidates[0];

  if (!opts.interactive) {
    throw new LocalCliError({
      code: "FOUNDRY_PROJECT_MULTIPLE",
      message: `Multiple Foundry projects found: ${candidates.map(formatProjectCandidate).join("; ")}.`,
      hint: `Pass --endpoint <url> to choose. Endpoints: ${candidates.map((c) => c.endpoint).join(" | ")}`,
    });
  }

  const endpoint = await select({
    message: `Multiple Foundry projects in subscription ${opts.subscriptionId}. Choose one:`,
    choices: candidates.map((c) => ({
      name: formatProjectCandidate(c),
      value: c.endpoint,
    })),
  });
  const chosen = candidates.find((c) => c.endpoint === endpoint);
  if (!chosen) {
    throw new LocalCliError({ code: "FOUNDRY_PROJECT_SELECTION_FAILED", message: "No project selected." });
  }
  return chosen;
}
